// Inbox slot — off-target classification banner (US-085, US-086, EPIC-07).
//
// Mounted by `<ThreadPanel>` (5c.2) whenever `conversation.offTarget` is true.
// The banner copy + behaviour bifurcates on the per-account `offTargetMode`
// setting from `voice.offTargetMode` / `account.offTargetMode`:
//
//   - `block`   → AI is forced to Review; banner reads "ne répondra pas".
//   - `suggest` → AI is forced to Review; AI synthesises a polite decline.
//   - `manual`  → "Ne rien faire" — treat normally (no AI action).
//
// The actual mode flip is owned by `derivePolicy(...)` in `lib/modePolicy.ts`
// (which delegates to `@barel/core` `resolveAiMode`). This component is
// strictly the explanatory banner.
//
// Design refs:
//   - `prototypes_and_docs/ls-inbox.jsx:473-504` — banner shape.
//   - `prototypes_and_docs/ls-voice.jsx:961-963`  — segmented stance options.

import { useT } from '@barel/i18n';
import type { OffTargetMode } from '@barel/core';
import { Banner, Button } from '@barel/ui';

import { offTargetCopyKey } from './lib/modePolicy';

export interface OffTargetHandlerProps {
  /** When `false` the component renders nothing. */
  visible: boolean;
  /** Per-account stance — drives the body copy. */
  offTargetMode: OffTargetMode;
  /** AI-classified reason ("recrutement", "spam", …). Falls back to a generic label. */
  reason?: string | null;
  /** Settings deep-link — opens the per-account voice editor. */
  onOpenSettings?: () => void;
}

export function OffTargetHandler({
  visible,
  offTargetMode,
  reason,
  onOpenSettings,
}: OffTargetHandlerProps) {
  const t = useT('inbox');
  if (!visible) return null;

  const copy = offTargetCopyKey(offTargetMode);
  const reasonText =
    reason && reason.trim().length > 0 ? reason : t('policy.offTarget.categorisedDefault');

  return (
    <Banner
      data-component="OffTargetHandler"
      data-testid={`inbox-off-target-${copy}-banner`}
      tone="neutral"
      title={t('policy.offTarget.title')}
      icon="🚫"
      action={
        onOpenSettings ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onOpenSettings}
            data-testid="inbox-off-target-settings-cta"
          >
            {t('policy.offTarget.openSettings')}
          </Button>
        ) : undefined
      }
    >
      <span data-testid="inbox-off-target-categorised">
        {t('policy.offTarget.categorisedAs', { reason: reasonText })}
      </span>{' '}
      <span data-testid={`inbox-off-target-${copy}-copy`}>{t(`policy.offTarget.${copy}`)}</span>
    </Banner>
  );
}
